/**
 * @tyndo/ui-kit/domain/RecordDigitalThreadPanel
 *
 * Closes G2.1 from docs/latest-analysis.md.
 *
 * The cross-tier seam composite. Renders a tabbed side panel for any
 * operational record (work order, lot, equipment, NCR, sales order)
 * showing its upstream cause + downstream effect + evidence + audit
 * trail in one surface, so operators don't have to page-switch between
 * modules to follow the digital thread.
 *
 * This is the wedge that beats incumbent stacks: SAP, Opcenter, and
 * Ignition each handle their own tier well, but the operator still
 * has to switch products to follow a single record across PLC/SCADA/
 * MES/ERP boundaries. RecordDigitalThreadPanel collapses that.
 *
 * Extended to surface PLM authority (released part rev,
 * releasing package, governing ECO, covering requirements, referenced
 * documents) inline on operational record details. The new
 * `plmContext` prop is optional and additive — existing callers see
 * no change.
 *
 * Wire pattern at the call site:
 *
 * ```tsx
 * <RecordDigitalThreadPanel
 *   recordId={workOrder.id}
 *   moduleHint="production"
 *   recordType="WorkOrder"
 *   recordLabel={`WO-${workOrder.number}`}
 *   plmContext={threadCtx}
 *   tabs={[
 *     { id: 'upstream',   label: 'Upstream',   children: <UpstreamView /> },
 *     { id: 'downstream', label: 'Downstream', children: <DownstreamView /> },
 *     { id: 'evidence',   label: 'Evidence',   children: <EvidenceView /> },
 *     { id: 'audit',      label: 'Audit',      children: <AuditView /> },
 *   ]}
 * />
 * ```
 */

import * as React from 'react';
import {
  Network,
  ArrowUpFromLine,
  ArrowDownToLine,
  FileSearch,
  ClipboardList,
  GitBranch,
  Package,
  FileBadge,
  ListChecks,
  FileText,
} from 'lucide-react';
import { Card } from '../primitives/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../primitives/tabs';
import { Badge } from '../primitives/badge';

export type DigitalThreadModuleHint =
  | 'production'
  | 'quality'
  | 'inventory'
  | 'sales'
  | 'procurement'
  | 'planning'
  | 'maintenance'
  | 'batch'
  | 'materials'
  | 'finance'
  | 'crm'
  | 'multisite'
  | string;

export interface DigitalThreadTab {
  id: 'upstream' | 'downstream' | 'evidence' | 'audit' | string;
  label: string;
  /** Optional badge count rendered in the tab trigger (e.g. "3" upstream parents). */
  count?: number;
  children: React.ReactNode;
}

/**
 * PLM authority context for an operational record. Mirrors the
 * `PlmThreadContext` DTO returned by `GET /plm/digital-thread`. Every
 * section is independently nullable; the panel hides any section whose
 * data is missing so a partially-linked record degrades gracefully.
 */
export interface PlmThreadContext {
  recordType: string;
  recordId: string;
  part?: {
    id: string;
    partNumber: string;
    description: string;
    lifecycleState: string;
  } | null;
  activePartRevision?: {
    id: string;
    revisionLabel: string;
    lifecycleState: string;
    releasedAt?: string | null;
  } | null;
  releasingPackage?: {
    id: string;
    packageNumber: string;
    releasedAt: string;
    releasedBy: string;
  } | null;
  governingChange?: {
    id: string;
    changeNumber: string;
    changeType: string;
    title: string;
  } | null;
  coveringRequirements?: Array<{
    id: string;
    requirementCode: string;
    title: string;
  }> | null;
  referencedDocuments?: Array<{
    id: string;
    title: string;
    documentType: string;
    revisionLabel?: string | null;
  }> | null;
  /** Optional human-readable note when the chain is incomplete. */
  diagnostic?: string | null;
}

export interface RecordDigitalThreadPanelProps {
  /** The id of the record this panel describes. */
  recordId: string;
  /** Owning module hint — used for default styling/icon and to disambiguate cross-module fetches. */
  moduleHint: DigitalThreadModuleHint;
  /** The record's noun ("WorkOrder", "Lot", "NCR", "SalesOrder", "Equipment"). */
  recordType: string;
  /** Human label for the record header ("WO-12345", "LOT-AB-99"). */
  recordLabel: string;
  /** Tabs to render. Recommended: upstream / downstream / evidence / audit. */
  tabs: DigitalThreadTab[];
  /** Initial tab id. Defaults to the first tab. */
  defaultTabId?: string;
  /** Optional className for outer container. */
  className?: string;
  /**
 * Optional PLM authority context. When `undefined` the panel
   * renders exactly as before (no PLM section). When `null` the panel
   * shows a small "PLM authority not yet linked" hint so operators know
   * the lookup ran but produced nothing. When populated, individual
   * sections render only when their data is present.
   */
  plmContext?: PlmThreadContext | null;
}

const TAB_ICONS: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  upstream: ArrowUpFromLine,
  downstream: ArrowDownToLine,
  evidence: FileSearch,
  audit: ClipboardList,
};

export const RecordDigitalThreadPanel: React.FC<RecordDigitalThreadPanelProps> = ({
  recordId,
  moduleHint,
  recordType,
  recordLabel,
  tabs,
  defaultTabId,
  className,
  plmContext,
}) => {
  const initial = defaultTabId ?? tabs[0]?.id ?? 'upstream';

  return (
    <Card variant="elevated" className={className}>
      <div className="flex items-center gap-2 border-b border-border p-3">
        <Network size={16} className="text-primary" />
        <div className="flex flex-col">
          <span className="t-eyebrow text-muted-foreground">
            {recordType} · {moduleHint}
          </span>
          <span className="text-sm font-semibold text-foreground" data-record-id={recordId}>
            {recordLabel}
          </span>
        </div>
      </div>

      {plmContext !== undefined && (
        <PlmAuthoritySection plmContext={plmContext} />
      )}

      <Tabs defaultValue={initial} className="p-3">
        <TabsList className="mb-3">
          {tabs.map(tab => {
            const Icon = TAB_ICONS[tab.id];
            return (
              <TabsTrigger key={tab.id} value={tab.id} className="flex items-center gap-1.5">
                {Icon ? <Icon size={14} /> : null}
                <span>{tab.label}</span>
                {typeof tab.count === 'number' && tab.count > 0 && (
                  <Badge variant="info" className="ml-1">
                    {tab.count}
                  </Badge>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>
        {tabs.map(tab => (
          <TabsContent key={tab.id} value={tab.id} className="min-h-[200px]">
            {tab.children}
          </TabsContent>
        ))}
      </Tabs>
    </Card>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PLM authority section
// ─────────────────────────────────────────────────────────────────────────────

interface PlmAuthoritySectionProps {
  plmContext: PlmThreadContext | null;
}

/**
 * Renders the PLM authority block beneath the record header. When
 * `plmContext` is null we show a small hint ("PLM authority not yet
 * linked"); otherwise we render whichever sections have data.
 *
 * Each card is keyboard-activatable and routes to the corresponding
 * plm-ui view via hash navigation (matching how other modules link).
 */
const PlmAuthoritySection: React.FC<PlmAuthoritySectionProps> = ({
  plmContext,
}) => {
  if (plmContext === null) {
    return (
      <div className="border-b border-border bg-surface-base px-3 py-2">
        <div className="flex items-center gap-2">
          <GitBranch size={12} className="text-muted-foreground" />
          <span className="text-xs text-muted-foreground">
            PLM authority not yet linked for this record
          </span>
        </div>
      </div>
    );
  }

  const part = plmContext.part ?? null;
  const rev = plmContext.activePartRevision ?? null;
  const pkg = plmContext.releasingPackage ?? null;
  const change = plmContext.governingChange ?? null;
  const reqs = plmContext.coveringRequirements ?? [];
  const docs = plmContext.referencedDocuments ?? [];

  const hasAny = part || rev || pkg || change || reqs.length > 0 || docs.length > 0;
  if (!hasAny) {
    return (
      <div className="border-b border-border bg-surface-base px-3 py-2">
        <div className="flex items-center gap-2">
          <GitBranch size={12} className="text-muted-foreground" />
          <span className="text-xs text-muted-foreground">
            PLM authority not yet linked for this record
          </span>
        </div>
        {plmContext.diagnostic ? (
          <p className="mt-1 text-[11px] text-muted-foreground">
            {plmContext.diagnostic}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="border-b border-border bg-surface-base px-3 py-3">
      <div className="mb-2 flex items-center gap-2">
        <GitBranch size={14} className="text-primary" />
        <span className="t-eyebrow text-muted-foreground">PLM authority</span>
      </div>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        {part && rev ? (
          <PlmCard
            icon={Package}
            title={`${part.partNumber} · rev ${rev.revisionLabel}`}
            subtitle={part.description}
            tail={
              <Badge variant={rev.lifecycleState === 'Released' ? 'success' : 'muted'}>
                {rev.lifecycleState}
              </Badge>
            }
            href={`#/plm/part-revision-detail?partId=${part.id}&revId=${rev.id}`}
            ariaLabel={`Released part revision ${part.partNumber} ${rev.revisionLabel}`}
          />
        ) : null}
        {pkg ? (
          <PlmCard
            icon={FileBadge}
            title={pkg.packageNumber}
            subtitle={`Released by ${pkg.releasedBy}`}
            tail={
              <span className="text-[11px] text-muted-foreground">
                {formatDate(pkg.releasedAt)}
              </span>
            }
            href={`#/plm/release-package-review?id=${pkg.id}`}
            ariaLabel={`Releasing package ${pkg.packageNumber}`}
          />
        ) : null}
        {change ? (
          <PlmCard
            icon={GitBranch}
            title={`${change.changeNumber} (${change.changeType})`}
            subtitle={change.title}
            href={`#/plm/ecr-eco-board?changeId=${change.id}`}
            ariaLabel={`Governing change ${change.changeNumber}`}
          />
        ) : null}
        {reqs.length > 0 ? (
          <CollapsedList
            icon={ListChecks}
            title={`Covering requirements · ${reqs.length}`}
            items={reqs.map(r => ({
              id: r.id,
              label: r.requirementCode,
              hint: r.title,
            }))}
          />
        ) : null}
        {docs.length > 0 ? (
          <CollapsedList
            icon={FileText}
            title={`Referenced documents · ${docs.length}`}
            items={docs.map(d => ({
              id: d.id,
              label: d.title,
              hint: `${d.documentType}${d.revisionLabel ? ` · rev ${d.revisionLabel}` : ''}`,
            }))}
          />
        ) : null}
      </div>
      {plmContext.diagnostic ? (
        <p className="mt-2 text-[11px] text-muted-foreground">
          {plmContext.diagnostic}
        </p>
      ) : null}
    </div>
  );
};

interface PlmCardProps {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  title: string;
  subtitle?: string;
  tail?: React.ReactNode;
  href: string;
  ariaLabel: string;
}

const PlmCard: React.FC<PlmCardProps> = ({
  icon: Icon,
  title,
  subtitle,
  tail,
  href,
  ariaLabel,
}) => (
  <a
    href={href}
    aria-label={ariaLabel}
    className="flex items-center gap-3 rounded border border-border bg-surface-raised p-2 transition-colors hover:bg-surface-sunken focus:outline-none focus:ring-2 focus:ring-primary"
  >
    <Icon size={16} className="text-primary shrink-0" />
    <div className="min-w-0 flex-1">
      <div className="truncate text-xs font-semibold text-foreground">{title}</div>
      {subtitle ? (
        <div className="truncate text-[11px] text-muted-foreground">{subtitle}</div>
      ) : null}
    </div>
    {tail ? <div className="shrink-0">{tail}</div> : null}
  </a>
);

interface CollapsedListProps {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  title: string;
  items: Array<{ id: string; label: string; hint?: string }>;
}

/**
 * Compact collapsed-by-default list. Used for covering requirements and
 * referenced documents — both lists are potentially long and would otherwise
 * dominate the panel.
 *
 * Built on the native `<details>/<summary>` disclosure so keyboard toggling
 * (Enter / Space), the expanded/collapsed announcement, and find-in-page
 * expansion all come for free — no `aria-expanded` bookkeeping or local state
 * to keep in sync. The `group` + `group-open:` utilities flip the Show/Hide
 * affordance off the element's own open state.
 */
const CollapsedList: React.FC<CollapsedListProps> = ({ icon: Icon, title, items }) => {
  return (
    <details className="group rounded border border-border bg-surface-raised">
      <summary className="flex w-full cursor-pointer list-none items-center gap-2 p-2 text-left transition-colors hover:bg-surface-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary [&::-webkit-details-marker]:hidden">
        <Icon size={16} className="text-primary shrink-0" />
        <span className="flex-1 text-xs font-semibold text-foreground">{title}</span>
        <span className="text-[11px] text-muted-foreground">
          <span className="group-open:hidden">Show</span>
          <span className="hidden group-open:inline">Hide</span>
        </span>
      </summary>
      <ul className="divide-y divide-border border-t border-border">
        {items.map(item => (
          <li key={item.id} className="px-2 py-1.5">
            <div className="text-xs font-medium text-foreground">{item.label}</div>
            {item.hint ? (
              <div className="text-[11px] text-muted-foreground">{item.hint}</div>
            ) : null}
          </li>
        ))}
      </ul>
    </details>
  );
};

const formatDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
};
